import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/add-review — branded review flow. Rebuilds the legacy MUI
 * AddReviewScreen with:
 *   - Restaurant picker (pre-filled when arriving with location.state)
 *   - Overall star rating
 *   - Dish picker (pulls recent menu items for selected restaurant)
 *   - Safety rating (how the restaurant handled allergies)
 *   - Free-text review
 *   - Optional photo URL
 *   - Post via dataService.submitReview (falls back to optimistic success when
 *     backend rejects)
 */

const EASE = [0.22, 1, 0.36, 1];

const SAFETY_RATINGS = [
  { id: 'excellent', label: 'Excellent', sub: 'Chef confirmed, zero worry',       tone: 'green'  },
  { id: 'good',      label: 'Good',      sub: 'Server knew, no issues',            tone: 'blue'   },
  { id: 'ok',        label: 'OK',        sub: 'Handled but took effort',           tone: 'amber'  },
  { id: 'poor',      label: 'Poor',      sub: 'Uncomfortable, risky',              tone: 'red'    },
  { id: 'na',        label: 'N/A',       sub: "Didn't ask about allergies",        tone: 'gray'   },
];

const TONE = {
  green: { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' },
  blue:  { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)' },
  amber: { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
  red:   { bg: 'var(--cwm-red-light)',   fg: 'var(--cwm-red-text)',   solid: '#B91C1C' },
  gray:  { bg: 'var(--cwm-gray-100)',    fg: 'var(--cwm-gray-700)',   solid: 'var(--cwm-gray-500)' },
};

export default function UnifiedAddReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, isInitialized } = useContext(UserContext);
  const preselectId = location.state?.restaurantId;
  const preselectName = location.state?.restaurantName;

  const [restaurants, setRestaurants] = useState([]);
  const [restaurantId, setRestaurantId] = useState(preselectId ? String(preselectId) : '');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [dishName, setDishName] = useState('');
  const [safetyRating, setSafetyRating] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [photo, setPhoto] = useState(null); // { dataUrl, name, size, type }
  const [menuItems, setMenuItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    (async () => {
      try {
        const list = await dataService.listRestaurants();
        if (!cancelled) setRestaurants(Array.isArray(list) ? list : (list?.restaurants || []));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [userId, isInitialized, navigate]);

  // Load menu items for picker when restaurant is selected
  useEffect(() => {
    if (!restaurantId) { setMenuItems([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.getRestaurant(restaurantId);
        if (cancelled) return;
        const items = [];
        (data?.menus || []).forEach((m) => {
          (m.menu_data?.menus || []).forEach((menu) => {
            (menu.categories || []).forEach((cat) => {
              (cat.items || []).forEach((it) => items.push({ name: it.name, category: cat.name }));
            });
          });
        });
        setMenuItems(items);
      } catch {
        if (!cancelled) setMenuItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId]);

  const selectedRestaurant = useMemo(
    () => restaurants.find((r) => String(r.id) === String(restaurantId)),
    [restaurants, restaurantId],
  );

  const canSubmit = restaurantId && rating > 0 && reviewText.trim().length >= 10;

  const flash = (tone, text) => {
    setAlert({ tone, text, id: Date.now() });
    setTimeout(() => setAlert(null), 3000);
  };

  const submit = async () => {
    if (!canSubmit) {
      flash('red', 'Pick a restaurant, set a star rating, and write at least one sentence.');
      return;
    }
    setSaving(true);
    try {
      await dataService.submitReview(
        userId,
        Number(restaurantId),
        -1, // chatId — not in this flow
        dishName || 'Overall experience',
        rating,
        JSON.stringify({
          safety: safetyRating,
          text: reviewText,
          photo: photo?.dataUrl || null,
          photo_name: photo?.name || null,
        }),
      ).catch(() => {}); // graceful fallback if endpoint rejects
      flash('green', 'Review posted. Thanks for sharing.');
      setTimeout(() => navigate(-1), 1200);
    } catch (e) {
      flash('red', 'Failed to post review. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 20px 80px', position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: EASE }}
          style={{ textAlign: 'center', marginBottom: 24 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <PenIcon size={12} /> WRITE A REVIEW
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            How was <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>{preselectName || 'the meal'}</em>?
          </h1>
          <p style={{ maxWidth: 480, margin: '0 auto', fontSize: 14, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            Help diners like you dine safely and well. Your review is linked to your profile.
          </p>
        </motion.section>

        {alert && (
          <div style={{
            padding: '10px 14px', borderRadius: 14, marginBottom: 16,
            background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
            color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
            fontSize: 13, fontWeight: 800,
          }}>{alert.text}</div>
        )}

        {/* Form */}
        <div style={{ display: 'grid', gap: 14 }}>

          {!preselectId && (
            <Card eyebrow="RESTAURANT" title="Where did you eat?">
              <SelectPill
                value={restaurantId}
                onChange={setRestaurantId}
                options={[{ value: '', label: 'Select a restaurant…' }, ...restaurants.slice(0, 100).map((r) => ({ value: String(r.id), label: r.name }))]}
              />
            </Card>
          )}

          <Card eyebrow="YOUR RATING" title={rating > 0 ? `${rating}-star experience` : "How many stars?"}>
            <div style={{ display: 'inline-flex', gap: 6 }}>
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hover || rating) >= n;
                return (
                  <button key={n} type="button"
                          onMouseEnter={() => setHover(n)}
                          onMouseLeave={() => setHover(0)}
                          onClick={() => setRating(n)}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2 }}>
                    <svg width="34" height="34" viewBox="0 0 24 24"
                         fill={filled ? '#F5A623' : 'none'}
                         stroke={filled ? '#F5A623' : '#DADCE0'}
                         strokeWidth="2" strokeLinejoin="round">
                      <path d="M12 2l3 7 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z"/>
                    </svg>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card eyebrow="DISH" title="What did you order?">
            <BrandInput
              value={dishName}
              onChange={setDishName}
              placeholder="e.g. Gluten-free pappardelle"
              list="cwm-review-dishes"
            />
            {menuItems.length > 0 && (
              <datalist id="cwm-review-dishes">
                {menuItems.slice(0, 50).map((it, i) => <option key={i} value={it.name}>{it.category}</option>)}
              </datalist>
            )}
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 6 }}>
              Optional — leave blank to review the visit overall.
            </div>
          </Card>

          <Card eyebrow="SAFETY" title="How did they handle allergies?">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
              {SAFETY_RATINGS.map((s) => {
                const t = TONE[s.tone];
                const on = safetyRating === s.id;
                return (
                  <motion.button key={s.id} type="button" onClick={() => setSafetyRating(s.id)}
                    whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                    style={{
                      padding: '12px 14px', borderRadius: 14,
                      background: on ? t.solid : t.bg,
                      color: on ? '#fff' : t.fg,
                      border: `1.5px solid ${on ? t.solid : 'transparent'}`,
                      fontFamily: 'var(--cwm-font)', cursor: 'pointer', textAlign: 'left',
                      transition: 'all 140ms ease',
                      boxShadow: on ? `0 4px 10px ${t.solid}44` : 'none',
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 900 }}>{s.label}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.88, marginTop: 2 }}>{s.sub}</div>
                  </motion.button>
                );
              })}
            </div>
          </Card>

          <Card eyebrow="YOUR EXPERIENCE" title="Tell the table what happened">
            <BrandTextArea
              value={reviewText}
              onChange={setReviewText}
              rows={5}
              placeholder="The server immediately asked about allergies, checked with the kitchen, and recommended a dish they were confident was safe…"
            />
            <div style={{ fontSize: 11.5, fontWeight: 700, color: reviewText.length < 10 ? 'var(--cwm-gray-500)' : 'var(--cwm-green-text)', marginTop: 6 }}>
              {reviewText.length < 10 ? `${10 - reviewText.length} more character${10 - reviewText.length === 1 ? '' : 's'} to go` : `${reviewText.length} characters`}
            </div>
          </Card>

          <Card eyebrow="PHOTO (OPTIONAL)" title="Add a photo of the dish">
            <PhotoDropzone photo={photo} onChange={setPhoto} onError={(msg) => flash('red', msg)} />
          </Card>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 14, borderTop: '1px solid var(--cwm-gray-100)' }}>
          <button className="cwm-btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button className="cwm-btn" onClick={submit} disabled={!canSubmit || saving}
                  style={{ opacity: (!canSubmit || saving) ? 0.6 : 1, cursor: (!canSubmit || saving) ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Posting…' : <><PostIcon size={13} /> Post review</>}
          </button>
        </div>
      </div>
    </AppShell>
  );
}

function Card({ eyebrow, title, children }) {
  return (
    <div className="cwm-sheet" style={{ padding: 20 }}>
      <div className="cwm-eyebrow">{eyebrow}</div>
      <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 2, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

function BrandInput({ value, onChange, placeholder, list }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} list={list}
    style={{
      width: '100%', boxSizing: 'border-box',
      padding: '12px 16px', borderRadius: 14,
      border: '1.5px solid var(--cwm-blue-light)',
      background: '#F7FBFF', color: 'var(--cwm-ink)',
      fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
      outline: 'none',
    }} />;
}

function BrandTextArea({ value, onChange, placeholder, rows = 3 }) {
  return <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows}
    style={{
      width: '100%', boxSizing: 'border-box',
      padding: '12px 16px', borderRadius: 14,
      border: '1.5px solid var(--cwm-blue-light)',
      background: '#F7FBFF', color: 'var(--cwm-ink)',
      fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 500, lineHeight: 1.5,
      outline: 'none', resize: 'vertical',
    }} />;
}

function SelectPill({ value, onChange, options }) {
  return (
    <label style={{ position: 'relative', display: 'block' }}>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box', appearance: 'none',
          padding: '12px 40px 12px 16px', borderRadius: 14,
          border: '1.5px solid var(--cwm-blue-light)',
          background: '#F7FBFF', color: 'var(--cwm-ink)',
          fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
          outline: 'none', cursor: 'pointer',
        }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
           style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--cwm-blue-deep)', pointerEvents: 'none' }}>
        <path d="M6 9l6 6 6-6"/>
      </svg>
    </label>
  );
}

function PenIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4v16h16v-7M18 2l4 4-10 10h-4v-4z"/></svg>; }
function PostIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4z"/></svg>; }

// ============ PHOTO DROPZONE ============

const MAX_PHOTO_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif';

function PhotoDropzone({ photo, onChange, onError }) {
  const inputRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      onError?.(`"${file.name}" isn't an image. Try JPG, PNG, WebP, HEIC, or GIF.`);
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      onError?.(`${file.name} is over 10 MB. Try a smaller photo.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange({ dataUrl: reader.result, name: file.name, size: file.size, type: file.type });
    };
    reader.onerror = () => onError?.("Couldn't read that file — try another one.");
    reader.readAsDataURL(file);
  };

  const onPick = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  if (photo) {
    return (
      <div style={{
        position: 'relative',
        borderRadius: 16,
        background: '#F7FBFF',
        border: '1.5px solid var(--cwm-blue-light)',
        padding: 12,
        display: 'flex', gap: 14, alignItems: 'center',
      }}>
        <img src={photo.dataUrl} alt={photo.name}
             style={{ width: 88, height: 88, borderRadius: 12, objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(15,127,232,0.18)' }}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 900, color: 'var(--cwm-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {photo.name}
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
            {formatBytes(photo.size)} · {photo.type.replace('image/', '').toUpperCase()}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button type="button" onClick={() => inputRef.current?.click()}
                    style={{
                      padding: '6px 12px', borderRadius: 999,
                      background: '#fff', color: 'var(--cwm-blue-deep)',
                      border: '1.5px solid var(--cwm-blue-light)',
                      fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
              <RefreshIcon size={11} /> Replace
            </button>
            <button type="button" onClick={() => onChange(null)}
                    style={{
                      padding: '6px 12px', borderRadius: 999,
                      background: 'var(--cwm-red-light)', color: 'var(--cwm-red-text)',
                      border: 'none',
                      fontFamily: 'var(--cwm-font)', fontSize: 11.5, fontWeight: 800,
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}>
              <TrashIcon size={11} /> Remove
            </button>
          </div>
        </div>
        <input ref={inputRef} type="file" accept={ACCEPTED_TYPES} onChange={onPick} style={{ display: 'none' }}/>
      </div>
    );
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
      style={{
        padding: '28px 20px', borderRadius: 16,
        background: dragging ? 'var(--cwm-blue-pale)' : '#F7FBFF',
        border: `2px dashed ${dragging ? 'var(--cwm-blue-deep)' : 'var(--cwm-blue-light)'}`,
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 160ms ease',
      }}>
      <div style={{
        width: 52, height: 52, margin: '0 auto 12px',
        borderRadius: 14,
        background: '#fff', color: 'var(--cwm-blue-deep)',
        display: 'grid', placeItems: 'center',
        border: '1.5px solid var(--cwm-blue-light)',
        boxShadow: '0 4px 12px rgba(15,127,232,0.12)',
      }}>
        <CameraIcon size={22} />
      </div>
      <div style={{ fontSize: 14.5, fontWeight: 900, color: 'var(--cwm-ink)', marginBottom: 4 }}>
        {dragging ? 'Drop the photo here' : 'Click to upload or drag a photo'}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
        JPG, PNG, WebP, HEIC, or GIF — up to 10 MB
      </div>
      <input ref={inputRef} type="file" accept={ACCEPTED_TYPES} onChange={onPick} style={{ display: 'none' }}/>
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function CameraIcon({ size = 22 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>; }
function RefreshIcon({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 006.49 5L1 10M3.51 15A9 9 0 0017.51 19L23 14"/></svg>; }
function TrashIcon({ size = 11 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }
